import { Button, IconButton, Collapse, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ScoreGrade, scoreColor, sentenceScoreAllSilence } from '../../Score';
import { AudioApi } from '../../api/AudioApi';
import { DialogPromptSummary } from '../../api/DialogPromptApi';
import { ISentenceScore, ScoreApi } from '../../api/ScoreApi';
import SpeakerBlueIcon from '../../asset/icon-speaker-blue.svg';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import LessonContext from '../../context/LessonContext';
import { useKaiTiFontStyles } from '../../font/KaiTiFont';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';
interface DialogPromptSummaryComponentProps {
    dialogPrompt: DialogPromptSummary;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

export const DialogPromptSummaryComponent: React.FC<DialogPromptSummaryComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const { lessonStartTime, setLessonStartTime } = React.useContext(LessonContext)


    console.log("component lessonStartTime", lessonStartTime);


    const [substitutedDialogPrompt, setSubstitutedDialogPrompt] = React.useState(dialogPrompt.prompt)
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [isQuestionAnswered, setIsQuestionAnswered] = React.useState(false);
    const [displaySummary, setDisplaySummary] = React.useState(false);
    const [chatBubbleResponseDone, setChatBubbleResponseDone] = React.useState(false);


    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);

    const [sentenceScoreList, setSentenceScoreList] = React.useState<ISentenceScore[]>([]);

    const kaiTiFontClass = useKaiTiFontStyles();

    // Capture lessonStartTime at mount to avoid re-triggering this effect after setLessonStartTime resets it
    const lessonStartTimeRef = React.useRef(lessonStartTime);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)

        const capturedStartTime = lessonStartTimeRef.current;
        let diffMinutes = -1;
        if (capturedStartTime) {
            const now = new Date();
            const diffMs = now.getTime() - capturedStartTime.getTime(); // difference in milliseconds
            diffMinutes = Math.ceil(diffMs / 60000); // 60000 ms = 1 minute
        }
        setSubstitutedDialogPrompt(prev => prev.replace(/<Minutes\/>/gi, String(diffMinutes)));

        ScoreApi.getAll(capturedStartTime)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to UserDialogPromptApi.completeDialogPrompt " + response.status)

            })
            .then(scoreList => {
                setSentenceScoreList(scoreList)

                const uniqueSentenceIds = new Set(scoreList.map(score => score.sentence_id));
                const sentenceCount = uniqueSentenceIds.size;
                setSubstitutedDialogPrompt(prev => prev.replace(/<SentenceCount\/>/gi, String(sentenceCount)));
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setLessonStartTime(new Date());
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearFooterList, dialogPrompt.prompt, history, location, setErrorMessage]);

    React.useEffect(() => {
        console.log("lessonStartTime was updated", lessonStartTime);
    }, [lessonStartTime]);

    const playSentenceScoreAudio = (sentenceScoreId: number) => {
        AudioApi.getSentenceScoreAudio(sentenceScoreId)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to ScoreApi.getScoreBySentence() " + response.status)
            })
            .then(audioData => playAudio(audioData))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }

    const handleYesButtonClick = React.useCallback(async () => {
        setIsQuestionAnswered(true);
        setDisplaySummary(true);
        clearFooterList();
    }, [clearFooterList]);

    const handleNoButtonClick = React.useCallback(() => {
        setIsQuestionAnswered(true);
        setDisplaySummary(false);
        clearFooterList();
    }, [clearFooterList]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    })


    React.useEffect(() => {
        if (chatBubbleDone) {
            setActionList([
                <Button variant="contained" color="secondary" onClick={() => handleNoButtonClick()} sx={{ ml: 4 }}>
                    {dialogPrompt.no_button_text}
                </Button>,
                <Button variant="contained" color="primary" onClick={() => handleYesButtonClick()} sx={{ ml: 4 }}>
                    {dialogPrompt.yes_button_text}
                </Button>
            ])
        }
    }, [chatBubbleDone, dialogPrompt.no_button_text, dialogPrompt.yes_button_text, handleNoButtonClick, handleYesButtonClick, setActionList]);

    React.useEffect(() => {
        if (chatBubbleResponseDone && displaySummary == false) {
            setDialogPromptComplete(dialogPrompt.id);
        } else if (isQuestionAnswered && displaySummary && sentenceScoreList) {
            setDialogPromptComplete(dialogPrompt.id);
        }

    }, [chatBubbleResponseDone, displaySummary, sentenceScoreList, dialogPrompt.id, isQuestionAnswered, setDialogPromptComplete])
    const groupedScores = React.useMemo(() => {
        const groups = new Map<number, ISentenceScore[]>();

        sentenceScoreList.forEach((score) => {
            const key = score.sentence_id;

            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key)!.push(score);
        });

        return Array.from(groups.entries()).map(([sentenceId, attempts]) => ({
            sentenceId,
            first: attempts[0],
            rest: attempts.slice(1),
        }));
    }, [sentenceScoreList]);

    const [expandedKeys, setExpandedKeys] = React.useState<Set<number>>(new Set());

    const toggle = (key: number) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const renderSentenceScore = (sentenceScore: ISentenceScore, expandButton?: React.ReactNode, reserveExpandSpace = false) => {
        if (sentenceScoreAllSilence(sentenceScore)) {
            return <div>Sentence Incomplete</div>;
        }

        return (
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: reserveExpandSpace ? '36px minmax(0, 1fr) 36px' : 'minmax(0, 1fr) 36px',
                    columnGap: 1,
                    alignItems: 'center',
                    maxWidth: '100%',
                }}
            >
                {reserveExpandSpace && (
                    <Stack alignItems="center" justifyContent="center">
                        {expandButton ?? <span style={{ width: 36, height: 36 }} />}
                    </Stack>
                )}

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ minWidth: 0 }}>
                    {sentenceScore.pinyin_list_spoken.map((pinyinElement, i) => (
                        <Stack key={`${sentenceScore.id}-${i}`} direction="column">
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    backgroundColor: `${scoreColor(sentenceScore.score_grade[i])}22`,
                                    borderTopLeftRadius: 4,
                                    borderTopRightRadius: 4,
                                    px: 1
                                }}
                            >
                                <Typography sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                                    {pinyinElement}
                                </Typography>
                                <Typography className={kaiTiFontClass.customFont} sx={{ fontSize: '24px' }}>
                                    {sentenceScore.text[i]}
                                </Typography>
                            </Stack>

                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    backgroundColor: `${scoreColor(ScoreGrade.Good)}22`,
                                    borderBottomLeftRadius: 4,
                                    borderBottomRightRadius: 4,
                                    px: 1
                                }}
                            >
                                <Typography sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                                    {sentenceScore.pinyin_list_transcript[i]}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>

                <IconButton aria-label="Play" onClick={() => playSentenceScoreAudio(sentenceScore.id)} sx={{ width: 36, height: 36, p: 0.75 }}>
                    <img src={SpeakerBlueIcon} alt="Play" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                </IconButton>
            </Stack>
        );
    };

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={substitutedDialogPrompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {isQuestionAnswered && displaySummary == false && <>
                <ChatBubble
                    isTeacher={false}
                    message={dialogPrompt.no_response}
                    messageAudio={promptAudio ? promptAudio : null}
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleResponseDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            </>}
            {isQuestionAnswered && displaySummary && <>
                <ChatBubble
                    isTeacher={false}
                    message={dialogPrompt.yes_response}
                    messageAudio={promptAudio ? promptAudio : null}
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleResponseDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            </>}
            {displaySummary && chatBubbleResponseDone && sentenceScoreList && <>
                <Stack alignItems="center" spacing={2}>
                {groupedScores.map((group) => {
                    const isExpanded = expandedKeys.has(group.sentenceId);

                    return (
                        <Stack key={group.sentenceId} direction="column" alignItems="center" spacing={1}>
                            <Stack direction="column" spacing={1} alignItems="flex-start">
                                {renderSentenceScore(
                                    group.first,
                                    group.rest.length > 0 ? (
                                        <IconButton
                                            aria-label={`${isExpanded ? 'Hide' : 'Show'} previous attempts for ${group.first.text}`}
                                            onClick={() => toggle(group.sentenceId)}
                                            sx={{ width: 36, height: 36, p: 0.75 }}
                                        >
                                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                    ) : undefined,
                                    group.rest.length > 0,
                                )}

                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Stack spacing={1}>
                                        {group.rest.map((attempt, idx) => (
                                            <Stack key={`${group.sentenceId}-${attempt.id ?? idx}`}>
                                                {renderSentenceScore(attempt, undefined, group.rest.length > 0)}
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Collapse>
                            </Stack>
                        </Stack>
                    );
                })}
                </Stack>
            </>}
        </Stack>
    );
};
